<script>
  import { onMount } from 'svelte';
  import { select } from 'd3-selection';
  import { scaleLinear } from 'd3-scale';
  import { keywordsList } from './settings';
  import NotePercent from './NotePercent.svelte';

  export let chartProps = {};
  export let data = [];
  let el;
  let tooltip;

  const minY = -0.05;
  const maxY = 1.05;
  let minX = 0.1;

  const help = `Ogni pallino corrisponde a una <em>keyword</em> assegnata a uno o più articoli. La posizione orizzontale del pallini rappresenta il numero di articoli a cui quella <em>keyword</em> è stata associata; la posizione verticale rappresenta la "vicinanza" a una o all'altra delle nostre protagoniste (<em>più in alto</em> significa che quella <em>keyword</em> appare più volte associata a <em>Meloni</em>, <em>più in basso a Schlein</em>). Se un pallino appare più scuro, significa che più <em>keyword</em> hanno lo stesso numero di ricorrenze e la stessa "distanza" dalle candidate. Sono mostrate solo le <em>keyword</em> con almeno due occorrenze.`;

  const clean = term => term.replace(/[^a-zA-Z0-9]/ig, '');

  const getColor = d => {
    if (d.x > 0.8) {
      return 'var(--meloni)';
    }
    if (d.x > 0.6) {
      return 'var(--shade-meloni)';
    }
    if (d.x > 0.4) {
      return 'var(--overlap)';
    }
    if (d.x > 0.2) {
      return 'var(--shade-schlein)';
    }
    return 'var(--schlein)';
  };

  const draw = () => {
    const container = select(el);
    container.html('');

    const realMaxX = Math.max(...data.map(d => d.y));
    const maxX = Math.ceil(realMaxX * 1.1) ;
    const maxW = Math.max(...data.map(d => d.weight));
    minX = -(Math.ceil(maxX * 0.025));
    const scaleX = scaleLinear()
      .range([0, chartProps.width - chartProps.margins.left - chartProps.margins.right])
      .domain([minX, maxX]);
    const scaleY = scaleLinear()
      .range([0, chartProps.height - chartProps.margins.top - chartProps.margins.bottom ])
      .domain([maxY, minY]);
    const scaleW = scaleLinear()
      .range([0.3, 0.9])
      .domain([1, maxW]);
    

    const svg = container.append('svg')
      .attr('width', chartProps.width)
      .attr('height', chartProps.height)
      .attr('viewbox', `0 0 ${chartProps.width} ${chartProps.height}`);

    // Headers
    const gh = svg.append('g')
    .attr('transform', `translate(${chartProps.margins.left}, 0)`);
    // Grid
    const gg = svg.append('g')
      .attr('transform', `translate(${chartProps.margins.left}, ${chartProps.margins.top})`);
    // Notes
    const gn = svg.append('g');
    // Legend
    const gl = svg.append('g')
      .attr('transform', `translate(${chartProps.margins.left}, ${chartProps.height - chartProps.margins.bottom + chartProps.margins.top})`);
    // Points 
    const gp = svg.append('g')
      .attr('transform', `translate(${chartProps.margins.left}, ${chartProps.margins.top})`);
    
    gh.append('text')
      .attr('x', (chartProps.width - chartProps.margins.right - chartProps.margins.left) / 2)
      .attr('y', chartProps.margins.top / 2)
      .attr('class', 'chart--header')
      .style('text-anchor', 'middle')
      .style('dominant-baseline', 'middle')
      .style('fill', 'var(--meloni)')
      .text('Giorgia Meloni');

    gh.append('text')
      .attr('x', (chartProps.width - chartProps.margins.right - chartProps.margins.left) / 2)
      .attr('y', chartProps.height - chartProps.margins.bottom + (chartProps.margins.top / 2))
      .attr('class', 'chart--header')
      .style('text-anchor', 'middle')
      .style('dominant-baseline', 'middle')
      .style('fill', 'var(--schlein)')
      .text('Elly Schlein');
    
    // Grid by values
    gg.append('line')
      .attr('x1', scaleX(minX))
      .attr('x2', scaleX(maxX))
      .attr('y1', scaleY(0.2))
      .attr('y2', scaleY(0.2))
      .style('stroke-width', 1)
      .style('stroke-dasharray', '1 4')
      .style('stroke', 'var(--dark)');
    gg.append('line')
      .attr('x1', scaleX(minX))
      .attr('x2', scaleX(maxX))
      .attr('y1', scaleY(0.4))
      .attr('y2', scaleY(0.4))
      .style('stroke-width', 1)
      .style('stroke-dasharray', '1 4')
      .style('stroke', 'var(--dark)');
    gg.append('line')
      .attr('x1', scaleX(minX))
      .attr('x2', scaleX(maxX))
      .attr('y1', scaleY(0.6))
      .attr('y2', scaleY(0.6))
      .style('stroke-width', 1)
      .style('stroke-dasharray', '1 4')
      .style('stroke', 'var(--dark)');
    gg.append('line')
      .attr('x1', scaleX(minX))
      .attr('x2', scaleX(maxX))
      .attr('y1', scaleY(0.8))
      .attr('y2', scaleY(0.8))
      .style('stroke-width', 1)
      .style('stroke-dasharray', '1 4')
      .style('stroke', 'var(--dark)');
    gg.append('line')
      .attr('x1', scaleX(minX))
      .attr('x2', scaleX(maxX))
      .attr('y1', scaleY(minY))
      .attr('y2', scaleY(minY))
      .style('stroke-width', 1)
      .style('stroke', 'var(--dark)');
    gg.append('line')
      .attr('x1', scaleX(minX))
      .attr('x2', scaleX(maxX))
      .attr('y1', scaleY(maxY))
      .attr('y2', scaleY(maxY))
      .style('stroke-width', 1)
      .style('stroke', 'var(--dark)');
    gg.append('text')
      .attr('x', scaleX(0))
      .attr('y', scaleY(minY))
      .attr('dy', 5)
      .style('text-anchor', 'start')
      .style('dominant-baseline', 'hanging')
      .attr('class', 'points--label')
      .text('0 occorrenze');
    gg.append('text')
      .attr('x', scaleX(0))
      .attr('y', scaleY(maxY))
      .attr('dy', -5)
      .style('text-anchor', 'start')
      .style('dominant-baseline', 'baseline')
      .attr('class', 'points--label')
      .text('0 occorrenze');
    gg.append('text')
      .attr('x', scaleX(maxX))
      .attr('y', scaleY(minY))
      .attr('dy', 5)
      .style('text-anchor', 'end')
      .style('dominant-baseline', 'hanging')
      .attr('class', 'points--label')
      .text(maxX);
    gg.append('text')
      .attr('x', scaleX(maxX))
      .attr('y', scaleY(maxY))
      .attr('dy', -5)
      .style('text-anchor', 'end')
      .style('dominant-baseline', 'baseline')
      .attr('class', 'points--label')
      .text(maxX);

    // Notes
    gn.append('text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('class', 'points--label')
      .attr('transform', `translate(${chartProps.margins.left / 2 - 2}, ${chartProps.height / 2}) rotate(-90)`)
      .text('↑ Keyword meno comuni ↑');
    gn.append('text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('class', 'points--label')
      .attr('transform', `translate(${chartProps.width - (chartProps.margins.left / 2) + 2}, ${chartProps.height / 2}) rotate(90)`)
      .text('↑ Keyword più comuni ↑');
    gn.append('text')
      .attr('x', chartProps.width / 2)
      .attr('y', chartProps.margins.top)
      .attr('dy', 5)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'hanging')
      .attr('class', 'points--label')
      .text('↑ Keyword associate più spesso a Meloni ↑');
    gn.append('text')
      .attr('x', chartProps.width / 2)
      .attr('y', chartProps.height - chartProps.margins.bottom)
      .attr('dy', -5)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'baseline')
      .attr('class', 'points--label')
      .text('↓ Keyword associate più spesso a Schlein ↓');

    // Data
    gp.selectAll('circle')
      .data(data)
      .enter()
      .append('circle')
      .attr('cx', d=> scaleX(d.y))
      .attr('cy', d=> scaleY(d.x))
      .attr('r', chartProps.radius)
      .attr('id', d => `cc-${clean(d.term)}`)
      .style('fill', getColor)
      .style('stroke', 'none')
      .style('stroke-width', 0)
      .style('opacity', d => scaleW(d.weight))
      .style('mix-blend-mode', 'multiply')
      .on('mouseenter', (e, d) => {
        select(`#cc-${clean(d.term)}`)
          .attr('class', 'selected');
        select(tooltip)
          .html(`<strong>${keywordsList(d.term)}</strong>Count: ${d.y}<br />Meloni: ${d.meloni}<br />Schlein: ${d.schlein}`)
          .style('top', `${scaleY(d.x) + chartProps.margins.top + chartProps.radius}px`)
          .style('left', `${scaleX(d.y) + chartProps.margins.left}px`)
          .attr('class', `tooltip ${(scaleX(d.y) + chartProps.margins.left) > (chartProps.width / 2) ? 'left' : 'right'}`);
      })
      .on('mouseout', (e, d) => {
        select(`#cc-${clean(d.term)}`)
          .attr('class', '');
        select(tooltip)
          .attr('class', 'tooltip')
          .html('');
      });

    gp.selectAll('text')
      .data(data.filter(d => d.y === realMaxX))
      .enter()
      .append('text')
      .attr('x', d=> scaleX(d.y))
      .attr('y', d=> scaleY(d.x))
      .attr('dy', (chartProps.radius + 2))
      .attr('dx', d => {
        if (d.y < maxX / 3) return -(chartProps.radius);
        if (d.y > ((maxX / 3) * 2)) return chartProps.radius;
        return 0;
      })
      .attr('class', 'points--label')
      .attr('id', d => `tt-${clean(d.term)}`)
      .style('dominant-baseline', 'hanging')
      .style('text-anchor', d => {
        if (d.y < maxX / 3) return 'start';
        if (d.y > ((maxX / 3) * 2)) return 'end';
        return 'middle';
      })
      .style('fill', getColor)
      .style('opacity', 1)
      .style('stroke', 'none')
      .text(d => d.term);

    const baseX = 16;
    const ll = [ 1, (maxW / 3), (maxW / 3 * 2), maxW ];
    ll.forEach((l, i) => {
      gl.append('circle')
        .attr('cx', baseX + chartProps.radius + (chartProps.radius * i))
        .attr('cy', chartProps.radius / 2)
        .attr('r', chartProps.radius)
        .style('fill', 'var(--neutral)')
        .style('stroke', 'var(--light)')
        .style('stroke-width', 1)
        .style('mix-blend-mode', 'multiply')
        .style('opacity', d => scaleW(l));
    });
    gl.append('text')
      .attr('x', 0)
      .attr('y', chartProps.radius / 2)
      .attr('class', 'points--label')
      .style('text-anchor', 'start')
      .style('dominant-baseline', 'middle')
      .text(1);
    gl.append('text')
      .attr('x', ll.length * chartProps.radius + baseX * 2)
      .attr('y', chartProps.radius / 2)
      .attr('class', 'points--label')
      .style('text-anchor', 'start')
      .style('dominant-baseline', 'middle')
      .text(`${maxW} Keyword con gli stessi valori`);
  };  

  onMount(() => {
    if (chartProps?.width > 0 && chartProps?.height > 0 && data.length) {
      draw();
    }
  });

  $: if (chartProps?.width > 0 && chartProps?.height > 0 && data.length) {
     draw();
  };
</script>

<div id="pointsHorizontal" style="height: {chartProps.height}px">
  <div class="chart" bind:this={el}></div>
  <div class="tooltip" bind:this={tooltip} />
</div>
<NotePercent text={help} />

<style>
  #pointsHorizontal {
    display: block;
    position: relative;
    width: 100%;
  }

  .chart {
    bottom: 0;
    display: block;
    height: 80vh;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
    z-index: 2;
  }
</style>